// Live preview of the engineer app inside the Branding settings tab.
// Structure mirrors the deployed mockup at /admin/settings (Branding tab):
// 500px max-width card, non-interactive, top nav (postcode pill + weekly gauge)
// + search bar + product card + Smart Basket bar + bottom nav.
//
// The preview reads the four colours from props rather than the CSS variables
// so it responds live to unsaved form edits without waiting for a save.
import {
  Package,
  MapPin,
  Pencil,
  Search as SearchIcon,
  ChevronRight,
  Truck,
  Zap,
  Home,
  ShoppingCart,
  FileText,
} from 'lucide-react'

interface BrandingLivePreviewProps {
  navBackgroundColor: string
  navTextColor: string
  primaryColor: string
  buttonTextColor: string
}

/**
 * Static, non-interactive preview of what the engineer app will look like
 * with the given four brand colours. Rendered inside the Branding tab.
 *
 * Colours are applied inline via ``style`` so the preview reflects unsaved
 * form edits immediately, independent of the CSS variables consumed by the
 * real engineer app.
 *
 * The tenant logo used to render on the left of the preview header, but it
 * was removed together with the real EngineerHeader logo (client feedback
 * 2026-07-15). Keeping the two headers structurally identical avoids the
 * preview drifting from what engineers actually see.
 */
export function BrandingLivePreview({
  navBackgroundColor,
  navTextColor,
  primaryColor,
  buttonTextColor,
}: BrandingLivePreviewProps) {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-lg max-w-[500px] mx-auto pointer-events-none select-none bg-white">
      {/* Header */}
      <div
        className="px-5 py-4 flex items-center justify-between"
        style={{ backgroundColor: navBackgroundColor, color: navTextColor }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border"
            style={{
              // Use nav-text derivatives (~25% + ~5% opacity) so the pill has
              // a visible outline whether the tenant picked a dark or light
              // nav background.
              borderColor: `${navTextColor}40`,
              backgroundColor: `${navTextColor}0D`,
            }}
          >
            <MapPin className="w-4 h-4" />
            <span className="font-medium">M14 6UH</span>
            <Pencil className="w-3.5 h-3.5 opacity-70" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-[11px] opacity-60 block leading-none">
              Weekly budget
            </span>
            <span className="text-xs font-bold">
              £22 <span className="opacity-60 font-normal">/ £30</span>
            </span>
          </div>
          <div className="w-10 h-10 relative">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="currentColor"
                strokeOpacity={0.2}
                strokeWidth="3"
              />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#fbbf24"
                strokeWidth="3"
                strokeDasharray="68.6 31.4"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">
              73%
            </span>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-5 py-5 bg-gray-50">
        <div className="relative">
          <SearchIcon
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5"
            style={{ color: primaryColor }}
          />
          <div className="w-full h-12 px-4 pr-12 rounded-xl border border-gray-300 bg-white flex items-center text-gray-400 text-sm">
            Search for products...
          </div>
        </div>
      </div>

      {/* Product card */}
      <div className="px-5 pb-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex gap-4">
            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-7 h-7 text-gray-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-bold text-sm leading-tight">
                Copper Tube 15mm x 3m
              </p>
              <span
                className="inline-block text-[11px] px-2 py-0.5 rounded-md mt-1.5 font-medium"
                style={{
                  backgroundColor: `${primaryColor}15`,
                  color: primaryColor,
                }}
              >
                Preferred
              </span>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
                <MapPin className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                <span>Nearest: 0.3 mi · 3 suppliers available</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span
                  className="font-bold text-base"
                  style={{ color: primaryColor }}
                >
                  from £12.50
                </span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              <Package className="w-3.5 h-3.5 text-gray-400" />
              <span className="font-medium text-gray-700">
                12 in stock, collect today
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              <Truck className="w-3.5 h-3.5 text-gray-400" />
              <span className="font-medium text-gray-700">Delivery tomorrow</span>
            </div>
          </div>
        </div>
      </div>

      {/* Smart Basket bar */}
      <div className="px-5 pb-5">
        <div className="flex items-center justify-between px-4 py-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div>
            <span className="text-gray-900 font-bold text-lg">£89.50</span>
            <span className="text-gray-500 text-sm ml-2">3 items</span>
          </div>
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-md"
            style={{ backgroundColor: primaryColor, color: buttonTextColor }}
          >
            <Zap className="w-4 h-4" />
            Smart basket
            <span className="bg-white/20 px-1.5 py-0.5 rounded text-[11px]">
              -£5.00
            </span>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div
        className="flex items-center justify-around py-4 border-t border-white/10"
        style={{ backgroundColor: navBackgroundColor }}
      >
        {[
          { icon: Home, label: 'Home', active: false },
          { icon: SearchIcon, label: 'Search', active: true },
          { icon: ShoppingCart, label: 'Basket', active: false },
          { icon: FileText, label: 'Orders', active: false },
        ].map(item => {
          const Icon = item.icon
          const color = item.active ? navTextColor : `${navTextColor}80`
          return (
            <div key={item.label} className="flex flex-col items-center gap-1">
              <Icon className="w-5 h-5" style={{ color }} />
              <span className="text-[10px]" style={{ color }}>
                {item.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
